// Admin directory model (2026-09-25). Pure functions over the getSchoolDirectory
// response, shared by the Students, Teachers, Levels, Usage and Accommodations
// pages. The response is compact: levels are arrays in `domainOrder`, and zero
// activity blocks are left out (read here as zero).

export const DOMAINS = [
  { id: 'algebra', label: 'Algebra', subject: 'math' },
  { id: 'advanced-math', label: 'Advanced Math', subject: 'math' },
  { id: 'problem-solving-and-data-analysis', label: 'Problem Solving and Data Analysis', subject: 'math' },
  { id: 'geometry-and-trigonometry', label: 'Geometry and Trigonometry', subject: 'math' },
  { id: 'information-and-ideas', label: 'Information and Ideas', subject: 'english' },
  { id: 'craft-and-structure', label: 'Craft and Structure', subject: 'english' },
  { id: 'expression-of-ideas', label: 'Expression of Ideas', subject: 'english' },
  { id: 'standard-english-conventions', label: 'Standard English Conventions', subject: 'english' },
];
export const SUBJECTS = [
  { id: 'math', label: 'Math' },
  { id: 'english', label: 'Reading and Writing' },
];
export const subjectLabel = id => SUBJECTS.find(s => s.id === id)?.label || 'All subjects';

// Chart colors (validated with the dataviz palette checker, 2026-09-25):
// Math and Reading and Writing pass every colorblind pair check; homework is the
// third categorical slot; all-subject totals use the admin/teacher olive accent.
export const COLORS = { all: '#536944', math: '#2a78d6', english: '#eb6834', homework: '#1baf7a' };

const EMPTY_RECENT = { answers: 0, practice: 0, homework: 0, activeSec: 0, activeDays: 0, mathAnswers: 0, mathSec: 0, englishAnswers: 0, englishSec: 0 };
const EMPTY_YEAR = { answers: 0, activeSec: 0, activeDays: 0, firstDay: null, lastDay: null };

// ---------- formatting ----------
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// A day string ('2026-09-24') is a calendar date, never shifted by time zone.
// A timestamp is shown in the viewer's local date. Always absolute: the year is
// added when it is not the current year, so old work never reads "288 days ago".
export function formatDate(value, { now = new Date(), withYear = false } = {}) {
  if (!value) return null;
  let y; let m; let d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    [y, m, d] = value.split('-').map(Number);
  } else {
    const t = new Date(value);
    if (!Number.isFinite(t.getTime())) return null;
    y = t.getFullYear(); m = t.getMonth() + 1; d = t.getDate();
  }
  const base = `${MONTHS[m - 1]} ${d}`;
  return withYear || y !== now.getFullYear() ? `${base}, ${y}` : base;
}

export function formatDateTime(value) {
  const t = new Date(value || '');
  if (!Number.isFinite(t.getTime())) return null;
  return `${formatDate(value, { withYear: true })}, ${t.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

export function formatMinutes(seconds) {
  const sec = Number(seconds) || 0;
  // Some time but under half a minute reads "<1 min", never a false "0 min".
  if (sec > 0 && sec < 30) return '<1 min';
  const minutes = Math.round(sec / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export const formatCount = n => (Number(n) || 0).toLocaleString('en-US');
// Share of a whole: "<1%" when there is some but it rounds to 0; blank when the whole is 0.
export function formatPercent(part, whole) {
  if (!whole) return '';
  const p = (part / whole) * 100;
  if (part > 0 && p < 1) return '<1%';
  return `${Math.round(p)}%`;
}
// One cell for 30-day activity: "None" when there was none, else answers + time.
export function activityText(answers, seconds) {
  if (!answers && !seconds) return 'None';
  if (!answers) return `${formatMinutes(seconds)}, no answers`;
  return `${plural(answers, 'answer')} · ${formatMinutes(seconds)}`;
}
// One level notation everywhere (critic round 1): "L5". A level of 3.6 counts as L3.
export const levelShort = v => (v == null ? null : `L${Math.min(7, Math.max(1, Math.floor(v)))}`);
export const plural = (n, one, many = `${one}s`) => `${formatCount(n)} ${n === 1 ? one : many}`;
export const gradeLabel = grade => (grade && grade !== 'Unknown' ? `Grade ${grade}` : 'Grade not set');
export const levelLabel = level => (level == null ? 'No level yet' : `Level ${Math.min(7, Math.max(1, Math.floor(level)))}`);
// Each level matches an SAT section score range: the College Board's
// performance score bands (Sandy 2026-09-25). Math and Reading and Writing
// differ at Levels 3 and 4. Level 1 is "200–360": the same range as "below
// 370", since a section score starts at 200.
export const LEVEL_SCORES = {
  math: ['200–360', '370–410', '420–460', '470–540', '550–600', '610–670', '680–800'],
  english: ['200–360', '370–410', '420–480', '490–540', '550–600', '610–670', '680–800'],
};
export const levelScore = (v, subject) => (v == null || !LEVEL_SCORES[subject] ? null
  : LEVEL_SCORES[subject][Math.min(7, Math.max(1, Math.floor(v))) - 1]);
// Plain text for CSVs and sentences: "L5 (SAT 550–600)".
export const levelScoreText = (v, subject) => (v == null ? null : `${levelShort(v)} (SAT ${levelScore(v, subject)})`);

// ---------- statuses ----------
export const STUDENT_STATUS = {
  checking: { label: 'Checking sign-in…', tone: 'muted' },
  'not-signed-in': { label: 'Not signed in yet', tone: 'warning' },
  'signed-in': { label: 'No practice in 30 days', tone: 'neutral' },
  practicing: { label: 'Practiced in 30 days', tone: 'good' },
};
export const TEACHER_STAGE = {
  checking: { label: 'Checking sign-in…', tone: 'muted', order: 0 },
  'not-signed-in': { label: 'Not signed in yet', tone: 'warning', order: 1 },
  'no-class': { label: 'Signed in, no class yet', tone: 'warning', order: 2 },
  'no-students': { label: 'Class made, no students yet', tone: 'neutral', order: 3 },
  'no-work': { label: 'Students joined, no work assigned', tone: 'neutral', order: 4 },
  assigning: { label: 'Assigning work', tone: 'good', order: 5 },
};

// Roster imports create accounts with a temporary password. "Not signed in yet"
// needs that password still in place AND no sign of use, because Google sign-in
// can skip the password change.
function studentStatus(student, temporary) {
  if (student.recent.answers > 0 || student.recent.activeSec > 0) return 'practicing';
  if (!temporary) return 'checking';
  const used = !!student.firstSeenAt || student.year.answers > 0 || student.year.activeSec > 0 || student.origin === 'join-class';
  return temporary.has(student.id) && !used ? 'not-signed-in' : 'signed-in';
}

function teacherStage(teacher, temporary) {
  // Roster imports give teachers classes before they ever sign in, so a class
  // is no proof of use; only work in their own class (or the app's tour flag) is.
  const ownsWork = teacher.classes.some(c => teacher.classIds.includes(c.id) && c.work.length);
  if (temporary && temporary.has(teacher.id) && !teacher.firstSeenAt && !ownsWork) return 'not-signed-in';
  if (teacher.classIds.length || teacher.coTeachClassIds.length) {
    if (!teacher.studentCount) return 'no-students';
    return teacher.workCount ? 'assigning' : 'no-work';
  }
  if (!temporary) return 'checking';
  return 'no-class';
}

function classSubject(work) {
  const subjects = new Set(work.map(w => w.subject).filter(Boolean));
  if (subjects.size === 2) return 'both';
  return [...subjects][0] || null;
}
export const classSubjectLabel = s => (s === 'both' ? 'Math + R&W' : s ? subjectLabel(s) : null);

// ---------- possible duplicate accounts ----------
// Roster imports made new accounts for people who already had one (a school
// email next to an older personal email). Same name (students: and same grade)
// = POSSIBLE duplicate. Two different people can share a name, so the pages say
// "possible" and never merge anything.
export const personKey = name => String(name || '').toLowerCase().replace(/[^a-z]/g, ' ').split(/\s+/).filter(Boolean).sort().join(' ');
function markDuplicates(people, keyOf) {
  const groups = new Map();
  for (const p of people) {
    const key = keyOf(p);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p.id);
  }
  for (const p of people) {
    const ids = groups.get(keyOf(p)) || [];
    p.possibleDuplicateIds = ids.length > 1 ? ids.filter(id => id !== p.id) : [];
  }
}

// ---------- staff test accounts ----------
// Brain Buffs and school staff make test classes and students ("test", "Period
// test", "Test Test", a class holding most of the school). They are hidden by
// default so they never top a ranking or add "+1 more" to real students.
const TEST_WORD = /\b(test|testing|demo|sandbox)\b/i;
const emailLocal = e => String(e || '').split('@')[0];
export function findTestAccounts(data) {
  const students = data.students || [];
  const size = new Map();
  for (const s of students) for (const id of s.classIds || []) size.set(id, (size.get(id) || 0) + 1);
  const big = Math.max(200, students.length * 0.4);
  const classes = new Set((data.classes || []).filter(c => TEST_WORD.test(c.name || '') || (size.get(c.id) || 0) > big).map(c => c.id));
  const teachers = new Set((data.teachers || []).filter(t => {
    if (TEST_WORD.test(t.name || '') || /(^|[._-])test/i.test(emailLocal(t.email))) return true;
    const owned = t.classIds || [];
    return owned.length > 0 && owned.every(id => classes.has(id));
  }).map(t => t.id));
  const studentIds = new Set(students.filter(s => TEST_WORD.test(s.name || '') || /(^|[._-])test/i.test(emailLocal(s.email))).map(s => s.id));
  return { classes, teachers, students: studentIds };
}

// ---------- the school's email domain ----------
// Sandy 2026-09-28: students whose own email is not on the school's domain
// (a personal Gmail or iCloud account, or a misspelled school address like
// "svvsd.ord") are hidden by default. The school domain is the one at least
// half of the students and teachers use; a public mail service never counts,
// so a school that signed up with Gmail hides nobody.
const PUBLIC_MAIL = new Set(['gmail.com', 'googlemail.com', 'icloud.com', 'me.com', 'mac.com', 'yahoo.com', 'ymail.com',
  'hotmail.com', 'outlook.com', 'live.com', 'msn.com', 'aol.com', 'proton.me', 'protonmail.com']);
export const emailDomain = email => /@([^@\s]+)$/.exec(String(email || '').trim().toLowerCase())?.[1] || null;
export function schoolEmailDomain(people) {
  const count = new Map();
  for (const p of people) {
    const d = emailDomain(p.email);
    if (d && !PUBLIC_MAIL.has(d)) count.set(d, (count.get(d) || 0) + 1);
  }
  const [top] = [...count].sort((a, b) => b[1] - a[1]);
  return top && top[1] * 2 >= people.length ? top[0] : null;
}
// The school domain itself or one under it ("students.svvsd.org").
export const onSchoolDomain = (email, domain) => {
  const d = emailDomain(email);
  return !!d && (d === domain || d.endsWith(`.${domain}`));
};

// A class name that repeats is made unique: within a teacher by period
// ("10 · A1"), across the school by teacher ("Advisory · Haggard").
const lastName = name => { const n = String(name || '').trim(); return n.includes(',') ? n.split(',')[0].trim() : n.split(/\s+/).at(-1) || n; };
function labelClasses(classes) {
  const byTeacher = new Map();
  for (const c of classes) {
    const key = `${c.teacherId}|${String(c.name).toLowerCase()}`;
    byTeacher.set(key, (byTeacher.get(key) || 0) + 1);
  }
  for (const c of classes) {
    const repeatsForTeacher = byTeacher.get(`${c.teacherId}|${String(c.name).toLowerCase()}`) > 1;
    c.label = repeatsForTeacher && c.period ? `${c.name} · ${c.period}` : c.name;
  }
  const bySchool = new Map();
  for (const c of classes) bySchool.set(c.label.toLowerCase(), (bySchool.get(c.label.toLowerCase()) || 0) + 1);
  for (const c of classes) {
    c.fullLabel = bySchool.get(c.label.toLowerCase()) > 1 && c.teacher ? `${c.label} · ${lastName(c.teacher.name)}` : c.label;
  }
}

// ---------- build ----------
export function buildDirectory(data, signIn = null, { includeTest = false, includeOtherEmails = false } = {}) {
  const order = Array.isArray(data?.domainOrder) ? data.domainOrder : DOMAINS.map(d => d.id);
  const domainIndex = new Map(order.map((id, i) => [id, i]));
  const tempStudents = signIn ? new Set(signIn.temporaryPassword?.students || []) : null;
  const tempTeachers = signIn ? new Set(signIn.temporaryPassword?.teachers || []) : null;
  const test = findTestAccounts(data);
  const keep = (set, id) => includeTest || !set.has(id);
  // Each student's own email decides. Only students: a teacher on a personal
  // email can own a real class, which would then read "No teacher".
  const realStudents = (data.students || []).filter(s => !test.students.has(s.id));
  const realTeachers = (data.teachers || []).filter(t => !test.teachers.has(t.id));
  const schoolDomain = schoolEmailDomain([...realStudents, ...realTeachers]);
  const otherEmailIds = new Set(schoolDomain ? realStudents.filter(s => !onSchoolDomain(s.email, schoolDomain)).map(s => s.id) : []);
  const keepStudent = id => keep(test.students, id) && (includeOtherEmails || !otherEmailIds.has(id));

  const teachers = new Map((data.teachers || []).filter(t => keep(test.teachers, t.id)).map(t => [t.id, {
    ...t, isTest: test.teachers.has(t.id),
    coTeachClassIds: (t.coTeachClassIds || []).filter(id => keep(test.classes, id)),
    classIds: (t.classIds || []).filter(id => keep(test.classes, id)),
  }]));
  const classes = new Map((data.classes || []).filter(c => keep(test.classes, c.id)).map(c => [c.id, {
    ...c, isTest: test.classes.has(c.id), coTeacherIds: (c.coTeacherIds || []).filter(id => keep(test.teachers, id)), work: c.work || [], studentIds: [],
  }]));

  const students = (data.students || []).filter(raw => keepStudent(raw.id)).map(raw => {
    const s = {
      ...raw,
      recent: { ...EMPTY_RECENT, ...(raw.recent || {}) },
      year: { ...EMPTY_YEAR, ...(raw.year || {}) },
      classIds: raw.classIds.filter(id => classes.has(id)),
    };
    // Levels by domain id; null = no level yet.
    s.levelOf = Object.fromEntries(DOMAINS.map(d => {
      const i = domainIndex.get(d.id);
      const level = i == null ? null : raw.levels?.[i];
      return [d.id, typeof level === 'number' ? level : null];
    }));
    s.startLevelOf = Object.fromEntries(DOMAINS.map(d => {
      const i = domainIndex.get(d.id);
      const level = i == null ? null : raw.startLevels?.[i];
      return [d.id, typeof level === 'number' ? level : null];
    }));
    s.subjectLevel = Object.fromEntries(SUBJECTS.map(sub => {
      const values = DOMAINS.filter(d => d.subject === sub.id).map(d => s.levelOf[d.id]).filter(v => v != null);
      return [sub.id, values.length ? values.reduce((a, b) => a + b, 0) / values.length : null];
    }));
    s.stuck = raw.stuck || [];
    s.teacherIds = [...new Set(s.classIds.flatMap(id => {
      const c = classes.get(id);
      return [c.teacherId, ...c.coTeacherIds];
    }))].filter(id => teachers.has(id));
    for (const id of s.classIds) classes.get(id).studentIds.push(s.id);
    s.lastActiveDay = s.year.lastDay || null;
    s.status = studentStatus(s, tempStudents);
    s.gradeLabel = gradeLabel(s.grade);
    s.isTest = test.students.has(s.id);
    return s;
  });
  markDuplicates(students, s => { const k = personKey(s.name); return k ? `${k}|${s.grade}` : ''; });
  const studentById = new Map(students.map(s => [s.id, s]));

  for (const c of classes.values()) {
    const members = c.studentIds.map(id => studentById.get(id));
    c.teacher = teachers.get(c.teacherId) || null;
    c.subject = classSubject(c.work);
    c.lastAssignedAt = c.work.map(w => w.createdAt).filter(Boolean).sort().at(-1) || null;
    c.practicing = members.filter(s => s.status === 'practicing').length;
    c.answers30 = members.reduce((sum, s) => sum + s.recent.answers, 0);
    c.seconds30 = members.reduce((sum, s) => sum + s.recent.activeSec, 0);
    c.grades = [...new Set(members.map(s => s.grade))].sort((a, b) => Number(a) - Number(b));
  }

  for (const t of teachers.values()) {
    const all = [...t.classIds, ...t.coTeachClassIds].map(id => classes.get(id)).filter(Boolean);
    const ids = new Set(all.flatMap(c => c.studentIds));
    const members = [...ids].map(id => studentById.get(id));
    t.classes = all;
    t.studentCount = ids.size;
    t.workCount = all.reduce((n, c) => n + c.work.length, 0);
    t.lastAssignedAt = all.map(c => c.lastAssignedAt).filter(Boolean).sort().at(-1) || null;
    t.practicing = members.filter(s => s.status === 'practicing').length;
    t.answers30 = members.reduce((sum, s) => sum + s.recent.answers, 0);
    t.seconds30 = members.reduce((sum, s) => sum + s.recent.activeSec, 0);
    t.stage = teacherStage(t, tempTeachers);
  }

  markDuplicates([...teachers.values()], t => personKey(t.name));
  labelClasses([...classes.values()]);
  const grades = [...new Set(students.map(s => s.grade))].sort((a, b) => (a === 'Unknown' ? 1 : b === 'Unknown' ? -1 : Number(a) - Number(b)));
  return {
    school: data.school,
    generatedAt: data.generatedAt,
    window: data.window,
    series: data.series,
    coverage: data.coverage,
    signInChecked: !!signIn,
    includeTest,
    hiddenTest: includeTest ? null : { students: test.students.size, teachers: test.teachers.size, classes: test.classes.size },
    // The school's email domain (null: none found, nothing hidden) and the
    // students not on it, hidden unless includeOtherEmails.
    schoolDomain,
    includeOtherEmails,
    otherEmailIds,
    // Every student left out on purpose (test accounts, other emails): pages
    // never count these as "not on your roster".
    hiddenStudentIds: new Set((data.students || []).map(s => s.id).filter(id => !keepStudent(id))),
    students,
    studentById,
    teachers: [...teachers.values()].sort(byName),
    teacherById: teachers,
    classes: [...classes.values()].sort((a, b) => byName(a.teacher || {}, b.teacher || {}) || byName(a, b)),
    classById: classes,
    grades,
  };
}

export const byName = (a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { numeric: true, sensitivity: 'base' });

// ---------- series ----------
const zeros = n => new Array(n).fill(0);
export function seriesFor(directory, scope = { type: 'school' }) {
  const s = directory.series || {};
  const n = (s.days || []).length;
  const empty = { activeStudents: zeros(n), answers: zeros(n), activeSec: zeros(n), mathAnswers: zeros(n), englishAnswers: zeros(n) };
  const pick = scope.type === 'grade' ? s.byGrade?.[scope.id]
    : scope.type === 'class' ? s.byClass?.[scope.id]
      : scope.type === 'teacher' ? s.byTeacher?.[scope.id]
        : s.school;
  return { days: s.days || [], ...empty, ...(pick || {}) };
}
// Homework answers carry no subject; the rest of the day is practice split by domain.
export const homeworkOf = (series, i) => Math.max(0, series.answers[i] - series.mathAnswers[i] - series.englishAnswers[i]);

// ---------- levels ----------
export function levelCounts(students, domainId) {
  const counts = zeros(7);
  let none = 0;
  for (const s of students) {
    const level = s.levelOf[domainId];
    if (level == null) none += 1;
    else counts[Math.min(6, Math.max(0, Math.floor(level) - 1))] += 1;
  }
  return { counts, none, placed: students.length - none, total: students.length };
}
export const inLevel = (student, domainId, level) => {
  const value = student.levelOf[domainId];
  return value != null && Math.min(7, Math.max(1, Math.floor(value))) === level;
};

// ---------- filtering + sorting ----------
// A name search (teacher and class searches, the Teacher and Class menus): case
// and accents do not count, and every typed word must be in the text, in any
// order ("lee 9" finds "Algebra 9 · Lee").
const fold = text => String(text ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export const matchesSearch = (text, query) => fold(query).split(/\s+/).filter(Boolean).every(word => fold(text).includes(word));

export function filterStudents(students, { q = '', grade = '', teacherId = '', classId = '', status = '', duplicates = false } = {}) {
  const needle = q.trim().toLowerCase();
  return students.filter(s => (!needle || `${s.name} ${s.email || ''}`.toLowerCase().includes(needle))
    && (!grade || s.grade === grade)
    && (!teacherId || s.teacherIds.includes(teacherId))
    && (!classId || s.classIds.includes(classId))
    && (!status || s.status === status)
    && (!duplicates || s.possibleDuplicateIds.length > 0));
}

export const PAGE_SIZE = 50;
// On a phone every row is a tall card, so a table shows at most 20 per page
// (50 Usage cards made a 26,000px page). Desktop page sizes are unchanged.
export const PHONE_PAGE_SIZE = 20;

// The rows a DataTable shows on one page. Anything that describes the table
// (Buffy's page data on the results page) uses this too, so it names the same rows.
export function tablePage(rows, page, phone = false, pageSize = PAGE_SIZE) {
  const size = phone ? Math.min(pageSize, PHONE_PAGE_SIZE) : pageSize;
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const current = Math.min(Math.max(1, Number(page) || 1), pages);
  const start = (current - 1) * size;
  return { size, pages, current, start, visible: rows.slice(start, start + size) };
}

export function sortRows(rows, sort, columns) {
  const column = columns.find(c => c.key === sort?.key);
  if (!column?.sortValue) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = column.sortValue(a);
    const y = column.sortValue(b);
    // Missing values always sort last, whatever the direction.
    if (x == null && y == null) return byName(a, b);
    if (x == null) return 1;
    if (y == null) return -1;
    const cmp = typeof x === 'string' ? x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' }) : x - y;
    return cmp * dir || byName(a, b);
  });
}

// ---------- export ----------
// Cells that start with = + - @ are prefixed so spreadsheet apps never run them.
export function csvCell(value) {
  if (value == null) return '';
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
export function toCsv(columns, rows) {
  const head = columns.map(c => csvCell(c.label)).join(',');
  const body = rows.map(r => columns.map(c => csvCell(c.csv ? c.csv(r) : r[c.key])).join(','));
  return [head, ...body].join('\r\n');
}
export function downloadCsv(filename, text) {
  const blob = new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export const fileStamp = (date = new Date()) => date.toISOString().slice(0, 10);
